import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { MealPlanEntry, MealType } from '@/types';
import {
  getMealPlanEntries,
  saveMealPlanEntry,
  deleteMealPlanEntry,
} from '@/lib/data-layer';
import { generateId } from '@/lib/utils';

interface MealPlanInput {
  id?: string;
  date: string; // YYYY-MM-DD
  mealType: MealType;
  recipeId?: string;
  customMealName?: string;
  servings: number;
  notes?: string;
}

interface MealPlanState {
  entries: MealPlanEntry[];
  isLoading: boolean;
  error: string | null;
  /** ISO timestamp of the last successful mutation — used to show a "Saved" indicator. */
  lastSyncedAt: string | null;
  loadEntries: () => Promise<void>;
  upsertEntry: (input: MealPlanInput) => Promise<MealPlanEntry>;
  removeEntry: (id: string) => Promise<void>;
  removeBySlot: (date: string, mealType: MealType) => Promise<void>;
  moveEntry: (entryId: string, targetDate: string, targetMealType: MealType) => Promise<'moved' | 'swapped' | 'noop'>;
  clearWeek: (weekStartDate: string) => Promise<number>;
  copyWeek: (sourceWeekStartDate: string, targetWeekStartDate: string) => Promise<number>;
  getEntry: (date: string, mealType: MealType) => MealPlanEntry | undefined;
  getEntriesForRange: (startDate: string, endDate: string) => MealPlanEntry[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

function toLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromIsoDate(dateString: string): Date {
  return new Date(`${dateString}T12:00:00`);
}

function addDays(dateString: string, days: number): string {
  const date = fromIsoDate(dateString);
  date.setDate(date.getDate() + days);
  return toLocalDate(date);
}

function getDayOffset(fromDate: string, toDate: string): number {
  const diffMs = fromIsoDate(toDate).getTime() - fromIsoDate(fromDate).getTime();
  return Math.round(diffMs / DAY_MS);
}

function sortEntries(entries: MealPlanEntry[]): MealPlanEntry[] {
  return [...entries].sort((a, b) => {
    const byDate = a.date.localeCompare(b.date);
    if (byDate !== 0) return byDate;
    return a.mealType.localeCompare(b.mealType);
  });
}

export const useMealPlanStore = create<MealPlanState>()(
  devtools(
    (set, get) => ({
      entries: [],
      isLoading: false,
      error: null,
      lastSyncedAt: null,

      loadEntries: async () => {
        set({ isLoading: true, error: null });
        try {
          const entries = await getMealPlanEntries();
          set({ entries: sortEntries(entries), isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load meal plans',
            isLoading: false,
          });
        }
      },

      upsertEntry: async (input) => {
        if (!input.recipeId && !input.customMealName?.trim()) {
          throw new Error('Either recipeId or customMealName is required');
        }

        const now = new Date().toISOString();
        const existingById = input.id ? get().entries.find((entry) => entry.id === input.id) : undefined;
        const existingBySlot = get().entries.find(
          (entry) => entry.date === input.date && entry.mealType === input.mealType
        );
        const existing = existingById ?? existingBySlot;

        const nextEntry: MealPlanEntry = {
          id: existing?.id ?? input.id ?? generateId(),
          date: input.date,
          mealType: input.mealType,
          recipeId: input.recipeId || undefined,
          customMealName: input.customMealName?.trim() || undefined,
          servings: Math.max(1, Math.round(input.servings || 1)),
          notes: input.notes?.trim() || undefined,
          createdAt: existing?.createdAt ?? now,
          updatedAt: now,
        };

        await saveMealPlanEntry(nextEntry);

        set((state) => {
          const nextEntries = state.entries.filter((entry) => {
            if (entry.id === nextEntry.id) return false;
            return !(entry.date === nextEntry.date && entry.mealType === nextEntry.mealType);
          });
          nextEntries.push(nextEntry);
          return { entries: sortEntries(nextEntries), lastSyncedAt: new Date().toISOString() };
        });

        return nextEntry;
      },

      removeEntry: async (id) => {
        await deleteMealPlanEntry(id);
        set((state) => ({
          entries: state.entries.filter((entry) => entry.id !== id),
          lastSyncedAt: new Date().toISOString(),
        }));
      },

      removeBySlot: async (date, mealType) => {
        const existing = get().entries.find((entry) => entry.date === date && entry.mealType === mealType);
        if (!existing) return;
        await get().removeEntry(existing.id);
      },

      moveEntry: async (entryId, targetDate, targetMealType) => {
        const sourceEntry = get().entries.find((entry) => entry.id === entryId);
        if (!sourceEntry) return 'noop';
        if (sourceEntry.date === targetDate && sourceEntry.mealType === targetMealType) {
          return 'noop';
        }

        const now = new Date().toISOString();
        const targetEntry = get().entries.find(
          (entry) =>
            entry.id !== entryId &&
            entry.date === targetDate &&
            entry.mealType === targetMealType
        );

        if (targetEntry) {
          const movedSource: MealPlanEntry = {
            ...sourceEntry,
            date: targetDate,
            mealType: targetMealType,
            updatedAt: now,
          };
          const movedTarget: MealPlanEntry = {
            ...targetEntry,
            date: sourceEntry.date,
            mealType: sourceEntry.mealType,
            updatedAt: now,
          };

          await Promise.all([saveMealPlanEntry(movedSource), saveMealPlanEntry(movedTarget)]);

          set((state) => {
            const nextEntries = state.entries.map((entry) => {
              if (entry.id === movedSource.id) return movedSource;
              if (entry.id === movedTarget.id) return movedTarget;
              return entry;
            });
            return { entries: sortEntries(nextEntries), lastSyncedAt: new Date().toISOString() };
          });

          return 'swapped';
        }

        const movedEntry: MealPlanEntry = {
          ...sourceEntry,
          date: targetDate,
          mealType: targetMealType,
          updatedAt: now,
        };

        await saveMealPlanEntry(movedEntry);
        set((state) => {
          const nextEntries = state.entries.map((entry) =>
            entry.id === movedEntry.id ? movedEntry : entry
          );
          return { entries: sortEntries(nextEntries), lastSyncedAt: new Date().toISOString() };
        });

        return 'moved';
      },

      clearWeek: async (weekStartDate) => {
        const weekEndDate = addDays(weekStartDate, 6);
        const targets = get().entries.filter(
          (entry) => entry.date >= weekStartDate && entry.date <= weekEndDate
        );
        await Promise.all(targets.map((entry) => deleteMealPlanEntry(entry.id)));
        set((state) => ({
          entries: state.entries.filter(
            (entry) => !(entry.date >= weekStartDate && entry.date <= weekEndDate)
          ),
          lastSyncedAt: new Date().toISOString(),
        }));
        return targets.length;
      },

      copyWeek: async (sourceWeekStartDate, targetWeekStartDate) => {
        const sourceWeekEndDate = addDays(sourceWeekStartDate, 6);
        const targetWeekEndDate = addDays(targetWeekStartDate, 6);
        const sourceEntries = get().entries.filter(
          (entry) => entry.date >= sourceWeekStartDate && entry.date <= sourceWeekEndDate
        );
        if (sourceEntries.length === 0) return 0;

        const sourceSlots = new Set(sourceEntries.map((entry) => `${getDayOffset(sourceWeekStartDate, entry.date)}|${entry.mealType}`));
        const targetEntriesToReplace = get().entries.filter((entry) => {
          if (entry.date < targetWeekStartDate || entry.date > targetWeekEndDate) return false;
          const offset = getDayOffset(targetWeekStartDate, entry.date);
          return sourceSlots.has(`${offset}|${entry.mealType}`);
        });

        await Promise.all(targetEntriesToReplace.map((entry) => deleteMealPlanEntry(entry.id)));

        const now = new Date().toISOString();
        const copiedEntries = sourceEntries.map((entry) => {
          const offset = getDayOffset(sourceWeekStartDate, entry.date);
          return {
            ...entry,
            id: generateId(),
            date: addDays(targetWeekStartDate, offset),
            createdAt: now,
            updatedAt: now,
          };
        });

        await Promise.all(copiedEntries.map((entry) => saveMealPlanEntry(entry)));

        set((state) => {
          const keptEntries = state.entries.filter((entry) => {
            if (entry.date < targetWeekStartDate || entry.date > targetWeekEndDate) return true;
            const offset = getDayOffset(targetWeekStartDate, entry.date);
            return !sourceSlots.has(`${offset}|${entry.mealType}`);
          });
          return {
            entries: sortEntries([...keptEntries, ...copiedEntries]),
            lastSyncedAt: new Date().toISOString(),
          };
        });

        return copiedEntries.length;
      },

      getEntry: (date, mealType) => {
        return get().entries.find((entry) => entry.date === date && entry.mealType === mealType);
      },

      getEntriesForRange: (startDate, endDate) => {
        return get().entries.filter((entry) => entry.date >= startDate && entry.date <= endDate);
      },
    }),
    { name: 'meal-plan-store' }
  )
);
